// Refuse-finalisation modal: manager picks a structured `RequestRefusalReason`
// and optionally attaches a free-text comment (max 1000 chars). Routes
// through the `refuse` transition (#585), which flips the row to REFUSED,
// clears any pre-selected winner, and stamps `closedAt`. The free-text
// comment lands on the row only — never on the audit ledger (the ledger
// records the reason code + a `hasRefusalComment` boolean) — so the
// comment is for downstream commercial review, not security audit.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { REFUSAL_COMMENT_MAX_LENGTH, RequestRefusalReason } from '@magimp/shared';
import { Button, Icon, Modal, Textarea } from '../../../../ui/index.js';
import { useRequestsStore } from '../../store.js';
import { useTransition } from '../../queries.js';
import { errorMessage } from '../../../../api/errors.js';
import type { RequestView, UserRef } from '../../types.js';

export interface RefuseRequestModalProps {
  request: RequestView;
  me: UserRef;
}

const REASON_OPTIONS: ReadonlyArray<{
  value: RequestRefusalReason;
  labelKey:
    | 'requests:refuseModal.reason.quoteResponseTooSlow'
    | 'requests:refuseModal.reason.priceNotAccepted'
    | 'requests:refuseModal.reason.clientUnresponsive'
    | 'requests:refuseModal.reason.other';
}> = [
  {
    value: RequestRefusalReason.QUOTE_RESPONSE_TOO_SLOW,
    labelKey: 'requests:refuseModal.reason.quoteResponseTooSlow',
  },
  {
    value: RequestRefusalReason.PRICE_NOT_ACCEPTED,
    labelKey: 'requests:refuseModal.reason.priceNotAccepted',
  },
  {
    value: RequestRefusalReason.CLIENT_UNRESPONSIVE,
    labelKey: 'requests:refuseModal.reason.clientUnresponsive',
  },
  { value: RequestRefusalReason.OTHER, labelKey: 'requests:refuseModal.reason.other' },
];

export function RefuseRequestModal({ request }: RefuseRequestModalProps) {
  const { t } = useTranslation(['requests', 'common']);
  const setModal = useRequestsStore((s) => s.setModal);
  const transitionMutation = useTransition(request.id);
  const onClose = () => setModal(null);
  const [reason, setReason] = useState<RequestRefusalReason | null>(null);
  const [comment, setComment] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const trimmedComment = comment.trim();
  const commentTooLong = trimmedComment.length > REFUSAL_COMMENT_MAX_LENGTH;
  const canSubmit = reason !== null && !commentTooLong;

  async function onSubmit(): Promise<void> {
    if (!canSubmit || reason === null) return;
    setSubmitError(null);
    try {
      const body: { reason: RequestRefusalReason; comment?: string } = { reason };
      if (trimmedComment.length > 0) body.comment = trimmedComment;
      await transitionMutation.mutateAsync({ key: 'refuse', body });
      setModal(null);
    } catch (err) {
      setSubmitError(errorMessage(err));
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('requests:refuseModal.title', { id: request.id })}
      width={520}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            kind="danger"
            icon="x"
            disabled={!canSubmit || transitionMutation.isPending}
            onClick={() => {
              void onSubmit();
            }}
          >
            {t('requests:refuseModal.confirm')}
          </Button>
        </>
      }
    >
      <div style={{ fontSize: 13, color: 'var(--fg-2)', marginBottom: 12 }}>
        {t('requests:refuseModal.explanation')}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {REASON_OPTIONS.map((opt) => {
          const selected = reason === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setReason(opt.value)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                borderRadius: 'var(--r-2)',
                textAlign: 'left',
                background: selected ? 'var(--accent-1-subtle)' : 'var(--bg-0)',
                border: `1px solid ${selected ? 'var(--accent-1)' : 'var(--border-1)'}`,
                cursor: 'pointer',
                fontSize: 13,
                color: 'var(--fg-1)',
              }}
            >
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  border: `2px solid ${selected ? 'var(--accent-1)' : 'var(--border-strong)'}`,
                  flex: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {selected && (
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: 'var(--accent-1)',
                    }}
                  />
                )}
              </span>
              {t(opt.labelKey)}
            </button>
          );
        })}
      </div>
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 11, color: 'var(--fg-3)', marginBottom: 5 }}>
          {t('requests:refuseModal.commentLabel')}
        </div>
        <Textarea
          value={comment}
          onChange={setComment}
          rows={4}
          placeholder={t('requests:refuseModal.commentPlaceholder')}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 4,
            fontSize: 11,
            color: commentTooLong ? 'var(--status-error-fg)' : 'var(--fg-3)',
          }}
        >
          <span>{t('requests:refuseModal.commentHint')}</span>
          <span>
            {trimmedComment.length} / {REFUSAL_COMMENT_MAX_LENGTH}
          </span>
        </div>
      </div>
      {submitError && (
        <div
          style={{
            marginTop: 12,
            fontSize: 12,
            color: 'var(--status-error-fg)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Icon name="alert-triangle" size={14} />
          <span>{submitError}</span>
        </div>
      )}
    </Modal>
  );
}
